import type { CalendarGroup as CalendarV2Group, CalendarUser, CalendarV2Item } from '@/services/calendarV2Api';

export type AssignableUser = CalendarUser;

export type CalendarGroup = CalendarV2Group;

export interface EventModalProps {
  mode: 'create' | 'edit';
  day?: Date;
  item?: CalendarV2Item;
  onSaved: () => void;
  onClose: () => void;
}

export const isValidTimezone = (value?: string | null): value is string => {
  if (!value || value === 'Local') return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
};

export const browserTimezone = (): string => {
  try {
    const value = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(value) ? value : '';
  } catch {
    return '';
  }
};

// Undefined means Intl's historical viewer-local behavior. Only persisted IANA
// zones are passed explicitly; nullable legacy events must not acquire UTC semantics.
export const presentationTimezone = (value?: string | null): string | undefined =>
  isValidTimezone(value) ? value : undefined;

export const toLocalDatetime = (iso?: string, timezone?: string | null): string => {
  if (!iso) return '';
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime()) || (timezone != null && !isValidTimezone(timezone))) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      ...(timezone ? { timeZone: timezone } : {}), year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(instant);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(value => value.type === type)?.value || '';
    return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
  } catch {
    return '';
  }
};

export const zonedDateKey = (iso: string, timezone?: string | null): string =>
  toLocalDatetime(iso, presentationTimezone(timezone)).slice(0, 10);

export const toISO = (datetimeLocal: string): string => {
  if (!datetimeLocal) return '';
  return new Date(datetimeLocal).toISOString();
};

// Today's date as a local `YYYY-MM-DD` string, used as a fallback when a time
// is entered before a date so the combined datetime-local value stays valid.
export const todayLocalDate = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export type RepeatFreq = 'none' | 'daily' | 'weekly' | 'fortnightly' | 'monthly';
export type RepeatEndMode = 'date' | 'count';
